/* eslint-disable complexity */

import { flattenTree } from '@futrli/shared-services';

import { FRForecastLayoutItem, FRForecastLayoutItemDisplayType, ForecastTreeNode } from '../../../types';
import { REPORT_SECTION, CATEGORY_NAME_INDEXES, SIZES } from '../../../../../../core/constants';
import { RowType } from '../../../../reports/data-grid/types';
import { ROW_KIND } from '../../../../reports/data-grid/constants';
import { ReportTotal } from '@futrli-sdk/predict';

/**
 * Converts the given layout / data to grid rows data
 * @param layout - The layout to apply
 * @param section - The report section this row belongs to (balance_sheet, Profit_and_loss etc)
 */
export const convertForecastLayoutToGridRows = (layout: FRForecastLayoutItem[], section: REPORT_SECTION) => {
  // Apply the layout to the tree
  const rows: RowType[] = [];

  layout.forEach(item => {
    const { nodes, category, displayType } = item;
    if (nodes?.length) {
      switch (displayType) {
        case FRForecastLayoutItemDisplayType.headingWithTopLevelAccounts: {
          rows.push(convertTreeNodeToHeadingRow(nodes[0], section, category));
          nodes[0].children.forEach(child => {
            rows.push(convertTreeNodeToTopLevelAccountRow(child, section, category));
            rows.push(...flattenTree(child.children).map(c => convertTreeNodeToAccountGridRow(c, section, category)));
          });
          break;
        }
        case FRForecastLayoutItemDisplayType.topLevelAccounts: {
          rows.push(convertTreeNodeToTopLevelAccountRow(nodes[0], section, category));
          rows.push(...flattenTree(nodes[0].children).map(c => convertTreeNodeToAccountGridRow(c, section, category)));
          break;
        }
        default: {
          rows.push(...flattenTree(nodes).map(n => convertTreeNodeToAccountGridRow(n, section, category)));
        }
      }
    }
  });

  return { totalRow: null, rows };
};

/**
 * Takes a tree node and converts it to a grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToAccountGridRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES
): RowType => {
  const rowDefaults = {
    section,
    isFirst: node.isFirst,
    isLast: node.isLast,
    padding: false,
    level: node.level,
    kind: ROW_KIND.account,
    childCount: node.childCount,
    isBold: false
  };

  const accountDefaults = {
    index: node.ref,
    name: node.name,
    canonical_category: category,
    period_to_date: node.period_to_date,
    period_data: node.row || []
  };

  // Always allowed node type / account or group with children
  if (isAlwaysAllowedNodeType(node.type) || node.childCount > 0) {
    return {
      ...rowDefaults,
      kind: ROW_KIND.organisation,
      isBold: true,
      account: {
        ...accountDefaults,
        type: ROW_KIND.group
      }
    };
  }
  // Account with no children
  return {
    ...rowDefaults,
    kind: ROW_KIND.account,
    account: {
      ...accountDefaults,
      type: ROW_KIND.account,
      unused_account: false,
      average_days_to_pay: node.average_days_to_pay || 0,
      system_account_role: node.system_account_role || null
    }
  };
};

/**
 * Takes a tree node and converts it to a total grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToHeadingRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES
): RowType => {
  return {
    section,
    isBold: true,
    headerBackground: '#cac9cd',
    kind: ROW_KIND.heading,
    name: node.name,
    account: {
      canonical_category: category,
      index: node.ref,
      name: node.name,
      period_data: node.row?.map(() => ' ') || [],
      type: ROW_KIND.total,
      period_to_date: node.period_to_date,
      static_id: node.static_id as ReportTotal['static_id']
    },
    padding: false,
    childCount: node.childCount,
    level: node.level,
    isFirst: node.isFirst,
    isLast: node.isLast,
    forceRowSize: SIZES.MEDIUM
  };
};

/**
 * Takes a tree node and converts it to a top level account grid row
 * @param node - The tree node to convert
 * @param section - The report section this row belongs to
 * @param category - The category of the account
 */
export const convertTreeNodeToTopLevelAccountRow = (
  node: ForecastTreeNode,
  section: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES
): RowType => {
  return {
    section,
    isBold: true,
    kind: ROW_KIND.total,
    name: node.name,
    subtitle: undefined,
    headerBackground: '#edeef1',
    account: {
      canonical_category: category,
      index: node.ref,
      name: node.name,
      period_data: node.row || [],
      type: ROW_KIND.total,
      period_to_date: node.period_to_date,
      static_id: node.static_id as ReportTotal['static_id']
    },
    disableLargeSize: false,
    padding: false,
    childCount: node.childCount,
    level: node.level,
    isFirst: node.isFirst,
    isLast: node.isLast,
    forceRowSize: null,
    hidePeriodToDateDifference: false
  };
};

/**
 * Returns true if the given node type is one of the always allowed (always shown) types
 * @param nodeType - node type to check
 */
export const isAlwaysAllowedNodeType = (nodeType: string) => {
  return ['top-level-static-name-only', 'static-line-calculation', 'static-account-calculation'].includes(nodeType);
};
